import { z } from 'zod';
import { BusinessUnitStandingSchema, UnitAccessSchema } from '../business-units/types';
import { PermissionEntrySchema } from '../system-roles/types';

/**
 * Impersonation status surfaced on /me. When `impersonating` is false, the
 * session is normal and `realUser` is omitted — keep the discriminated shape
 * so callers can narrow without nullability gymnastics.
 */
export const MeImpersonationSchema = z.union([
  z.object({ impersonating: z.literal(false) }),
  z.object({
    impersonating: z.literal(true),
    // `realUser` is the OPERATOR (the human pressing buttons). The top-level
    // /me `data` continues to be the IMPERSONATED user.
    realUser: z.lazy(() => UserSchema),
  }),
]);
export type MeImpersonation = z.infer<typeof MeImpersonationSchema>;

export const MeRelatedSchema = z.object({
  permissions: z.array(PermissionEntrySchema),
  impersonation: MeImpersonationSchema,
  /**
   * The caller's system-role NAME — "Administrator", "Super Admin", whatever
   * the tenant called theirs. `null` when the id on the row resolves to no
   * role, which must not read as any particular role.
   *
   * A label for `data`, not a field of it: `data` is the shared `User` shape
   * every other user endpoint returns. Access is decided by `permissions`;
   * this is for the rare gate that is about the role itself, such as a
   * preview held back to Administrator until it is ready for everyone.
   *
   * `nullish`, so an ABSENT key parses as well as a null one. `nullable`
   * alone would not: it admits `null` and rejects a missing key. This field
   * was added to a response shape that already existed, and the web and the
   * API deploy as separate services — so a consumer validating `/me` against
   * this schema will meet an API that predates the field. It has to read as
   * "no name", the way `useMe` already treats it, rather than as a ZodError.
   */
  systemRoleName: z.string().nullish(),
  /** The business units the caller can act in, with their role in each.
   *  `nullish` for the same reason as `systemRoleName`. */
  businessUnits: z.array(BusinessUnitStandingSchema).nullish(),
});
export type MeRelated = z.infer<typeof MeRelatedSchema>;

const CustomFieldsRecord = z.record(z.string(), z.unknown());

/** Canonical employee-type values — keep in sync with the API zod enum. */
export const EMPLOYEE_TYPES = [
  'Fixed Pay',
  'Consultant (Fixed)',
  'Consultant (Variable)',
  'Intern',
] as const;
export const EmployeeTypeSchema = z.enum(EMPLOYEE_TYPES);
export type EmployeeType = z.infer<typeof EmployeeTypeSchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  baUserId: z.string().nullable(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /** Optional display / Quickbooks-matching name. Falls back to
   *  firstName + lastName in the FE when null. */
  preferredName: z.string().nullable(),
  title: z.string().nullable(),
  historicalTimesheetId: z.string().nullable(),
  systemRoleId: z.string().uuid(),
  serviceRoleId: z.string().uuid(),
  /** Their primary employer — the business unit that holds their HR record. */
  businessUnitId: z.string().uuid(),
  managerId: z.string().uuid().nullable(),
  srControllerId: z.string().uuid().nullable(),
  departmentId: z.string().uuid().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  workPhone: z.string().nullable(),
  homePhone: z.string().nullable(),
  cellPhone: z.string().nullable(),
  /** ISO calendar date (YYYY-MM-DD) or null. Drives the derived Status. */
  startDate: z.string().nullable(),
  terminatedDate: z.string().nullable(),
  // Drizzle returns numeric() as string to preserve precision; clients can
  // coerce as needed.
  hoursPerMonth: z.string().nullable(),
  billRate: z.string().nullable(),
  employeeType: EmployeeTypeSchema.nullable(),
  kekaEmployeeId: z.string().nullable(),
  quickbooksId: z.string().nullable(),
  location: z.string().nullable(),
  active: z.boolean(),
  /** IANA tz id (e.g. 'America/Los_Angeles'). Defaults to 'UTC' until the
   *  web app sends the browser-detected zone via PATCH /api/users/me. */
  timezone: z.string(),
  customFields: CustomFieldsRecord,
  createdAt: z.string(),
  updatedAt: z.string(),
});
/** Base user row — the shape of get/create/patch/me responses. The list
 *  endpoint returns {@link UserWithLabelsSchema}. */
export type User = z.infer<typeof UserSchema>;

/**
 * A user enriched with display labels for its foreign keys, resolved
 * server-side. Returned by the list endpoint so the users table never
 * client-joins a capped entity list (which silently dropped names past the
 * page limit). The labels are always present on list responses; `null` =
 * the referenced entity couldn't be resolved (deleted) → render "—". Kept
 * distinct from the base `User` so a consumer can't mistake an un-enriched
 * write response for "labels missing".
 */
export const UserWithLabelsSchema = UserSchema.extend({
  systemRoleName: z.string().nullable(),
  serviceRoleName: z.string().nullable(),
  departmentName: z.string().nullable(),
  managerName: z.string().nullable(),
  /** The Business Units they work in: their primary employer first, then the
   *  units they were granted — or every unit, when their role holds All
   *  Business Units. */
  businessUnits: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      access: UnitAccessSchema,
    }),
  ),
});
export type UserWithLabels = z.infer<typeof UserWithLabelsSchema>;

const UserBody = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  preferredName: z.string().max(240).nullable().optional(),
  title: z.string().max(120).nullable().optional(),
  historicalTimesheetId: z.string().max(120).nullable().optional(),
  systemRoleId: z.string().uuid(),
  serviceRoleId: z.string().uuid(),
  managerId: z.string().uuid().nullable().optional(),
  srControllerId: z.string().uuid().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  addressLine1: z.string().max(200).nullable().optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  state: z.string().max(120).nullable().optional(),
  zip: z.string().max(40).nullable().optional(),
  country: z.string().max(120).nullable().optional(),
  workPhone: z.string().max(40).nullable().optional(),
  homePhone: z.string().max(40).nullable().optional(),
  cellPhone: z.string().max(40).nullable().optional(),
  startDate: z.string().nullable().optional(),
  terminatedDate: z.string().nullable().optional(),
  hoursPerMonth: z.number().nonnegative().nullable().optional(),
  billRate: z.number().nonnegative().nullable().optional(),
  employeeType: EmployeeTypeSchema.nullable().optional(),
  // Keka employee id — required and unique per tenant (uniqueness enforced
  // by the DB partial-unique index).
  kekaEmployeeId: z.string().trim().min(1).max(120),
  quickbooksId: z.string().max(120).nullable().optional(),
  location: z.string().nullable().optional(),
  active: z.boolean().optional(),
  timezone: z.string().min(1).max(64).optional(),
  customFields: CustomFieldsRecord.optional(),
});

export const CreateUserInput = UserBody.extend({
  /** Primary employer; optional while the tenant has one business unit. */
  businessUnitId: z.string().uuid().optional(),
});
export type CreateUserInput = z.infer<typeof CreateUserInput>;

// Email is omitted from patch — change-email is a separate flow.
export const PatchUserInput = UserBody.omit({ email: true }).partial();
export type PatchUserInput = z.infer<typeof PatchUserInput>;

// Dedicated change-email payload (`PATCH /api/users/:id/email`). Kept apart
// from PatchUserInput because the server updates the login identity
// (`ba_user.email`) alongside `users.email` on this endpoint only.
export const ChangeEmailInput = z.object({
  email: z.string().email().max(320),
});
export type ChangeEmailInput = z.infer<typeof ChangeEmailInput>;

export interface ListUsersQuery {
  q?: string;
  /** The people who work in this business unit: its employees and those
   *  granted access to it. */
  businessUnitId?: string;
  active?: boolean;
  systemRoleId?: string;
  serviceRoleId?: string;
  managerId?: string;
  departmentId?: string;
  employeeType?: EmployeeType;
  /** Optional single custom-field filter — see customers/types.ts. */
  customFieldId?: string;
  customFieldValue?: string;
  sortField?:
    | 'firstName'
    | 'lastName'
    | 'preferredName'
    | 'email'
    | 'title'
    | 'startDate'
    | 'terminatedDate'
    | 'createdAt'
    | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Employee export filter. Mirrors the list-page filters (status, role,
 * manager, custom fields, search text, sort) so the downloaded file matches
 * what the user sees on screen. Subset of `ListUsersQuery` minus pagination —
 * the export drains the whole filtered result.
 */
export type ExportUsersQuery = Pick<
  ListUsersQuery,
  | 'q'
  | 'active'
  | 'systemRoleId'
  | 'serviceRoleId'
  | 'managerId'
  | 'departmentId'
  | 'employeeType'
  | 'customFieldId'
  | 'customFieldValue'
  | 'sortField'
  | 'sortDirection'
>;

/**
 * SR Controller picker query — `ListUsersQuery` minus `active` (the endpoint
 * rejects it; the active check is embedded server-side and scoped to only
 * one of the two candidate branches — see `srControllerCandidatesSchema` on
 * the API) plus `includeId`, the record being edited, so that user is
 * always offered as a candidate alongside established Sr. Controllers (see
 * `UsersClient.listSrControllerCandidates`).
 */
export type ListSrControllerCandidatesQuery = Omit<ListUsersQuery, 'active'> & {
  includeId?: string;
};

export interface AddSkillInput {
  skillId: string;
}

export interface BulkAddSkillsInput {
  skillIds: string[];
}

// ---- Import / Export ------------------------------------------------------

/** Result of POST /api/users/import — one entry per row in the
 *  uploaded file (in source order). Mirrors the products shape; the
 *  generic `ImportSheet` consumes either. */
export type ImportUserRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportUsersRow {
  rowNumber: number;
  /** Email parsed from the row, used as the upsert key. Null when the
   *  row was missing/garbled. */
  email: string | null;
  outcome: ImportUserRowOutcome;
  errors?: string[];
}

export interface ImportUsersResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportUsersRow[];
}
